import React from 'react'
import Link from 'next/link'
import { motion } from 'framer-motion'
import { FaArrowLeft } from 'react-icons/fa'

/**
 * Reusable layout component for legal/terms pages
 * @param {string} title - Page title
 * @param {string} subtitle - Page subtitle/description
 * @param {React.ReactNode} icon - Icon component to display
 * @param {string} iconBgColor - Tailwind gradient class for icon background
 * @param {string} heroBgColor - Tailwind gradient class for hero background
 * @param {React.ReactNode} children - Page content
 * @param {string} backLink - Link to go back to (default: /terms)
 * @param {string} backLabel - Label for back link (default: Back to Terms)
 */
export const LegalPageLayout = ({ 
  title, 
  subtitle, 
  icon: Icon, 
  iconBgColor = 'from-orange-500 to-amber-500',
  heroBgColor = 'from-slate-900 via-slate-800 to-orange-900',
  children,
  backLink = '/terms',
  backLabel = 'Back to Terms'
}) => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className={`bg-linear-to-br ${heroBgColor} py-16 md:py-20`}>
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Back Link */}
            <Link 
              href={backLink} 
              className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-6 text-sm"
            >
              <FaArrowLeft />
              {backLabel}
            </Link>
            
            <div className="flex items-center gap-4 mb-4">
              {Icon && (
                <div className={`w-14 h-14 bg-linear-to-br ${iconBgColor} rounded-xl flex items-center justify-center shadow-lg`}>
                  <Icon className="text-2xl text-white" />
                </div>
              )}
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-black text-white">
                {title}
              </h1>
            </div>
            
            {subtitle && (
              <p className="text-lg text-slate-300 max-w-2xl">
                {subtitle}
              </p>
            )}
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {children}
        </motion.div>
      </div>
    </div>
  )
}

/**
 * Section card component for legal pages
 */
export const LegalSection = ({ title, children, className = '' }) => (
  <div className={`bg-white rounded-xl p-6 border border-slate-200 mb-6 ${className}`}>
    {title && <h2 className="text-lg font-bold text-slate-900 mb-4">{title}</h2>}
    <div className="text-slate-600 leading-relaxed">{children}</div>
  </div>
)

/**
 * Highlighted info box
 */
export const LegalHighlight = ({ children, color = 'blue' }) => {
  const colors = {
    blue: 'bg-blue-50 border-blue-200',
    orange: 'bg-orange-50 border-orange-200',
    green: 'bg-green-50 border-green-200',
    amber: 'bg-amber-50 border-amber-200',
    red: 'bg-red-50 border-red-200',
  }
  
  return (
    <div className={`${colors[color]} border rounded-xl p-6 mb-6`}>
      <div className="text-slate-700 leading-relaxed">{children}</div>
    </div>
  )
}

/**
 * Bullet list with colored bullets
 */
export const LegalList = ({ items, bulletColor = 'orange' }) => {
  const colors = {
    orange: 'text-orange-500',
    blue: 'text-blue-500',
    green: 'text-green-500',
    amber: 'text-amber-500',
  }
  
  return (
    <ul className="space-y-3">
      {items.map((item, index) => (
        <li key={index} className="flex gap-3">
          <span className={`${colors[bulletColor]} mt-1`}>•</span>
          <span className="text-slate-600">{item}</span>
        </li>
      ))}
    </ul>
  )
}

export default LegalPageLayout
